"use client";

import React, { useEffect, useState } from "react";
import { 
  Loader, 
  Tag, 
  Layers, 
  Check, 
  CircleAlert,
  Share2,
  Heart,
  Package,
  Truck,
  MessageCircle
} from "lucide-react";
import { 
  EcommerceProductsProvider, 
  useEcommerceProducts 
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";



function ProductSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
      <div className="aspect-square bg-muted rounded-xl w-full" />
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded w-3/4" />
        <div className="h-6 bg-muted rounded w-1/4" />
        <div className="h-24 bg-muted rounded w-full" />
        <div className="h-12 bg-muted rounded w-1/3" />
        <div className="h-12 bg-muted rounded w-full" />
      </div>
    </div>
  );
}



function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) return <ProductSkeleton />;
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <CircleAlert className="size-12 text-destructive mb-4" />
        <h2 className="text-2xl font-serif font-bold text-foreground">Errore nel caricamento</h2>
        <p className="text-muted-foreground">Non siamo riusciti a caricare i dettagli del prodotto.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Package className="size-12 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-serif font-bold text-foreground">Prodotto non trovato</h2>
        <p className="text-muted-foreground">Il prodotto che stai cercando non è disponibile al momento.</p>
        <Button asChild className="mt-6" variant="outline">
          <Link to="/CatalogPage">Torna al Catalogo</Link>
        </Button>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount = product.discounted_price !== undefined && product.discounted_price < product.price;
  const currentPrice = hasDiscount ? product.discounted_price : product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
      {}
      <div className="relative group">
        <div className="aspect-square overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
          {product.featured_image_url ? (
            <img 
              src={product.featured_image_url} 
              alt={product.title} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
              <span className="text-lg font-medium">Immagine non disponibile</span>
            </div>
          )}
        </div>
        {hasDiscount && (
          <Badge className="absolute top-4 left-4 bg-destructive text-white px-3 py-1 text-sm font-medium shadow-md">
            In Offerta
          </Badge>
        )}
      </div>

      {}
      <div className="flex flex-col space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {product.categories && product.categories.length > 0 && (
              <Badge variant="secondary" className="text-xs font-normal">
                {product.categories[0].name}
              </Badge>
            )}
            {hasDiscount && (
              <Badge variant="destructive" className="text-xs font-normal">
                Saldi
              </Badge>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-2">
            {product.title}
          </h1>
        </div>

        {product.short_description && (
          <div 
            className="prose prose-stone text-muted-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <Separator className="my-2" />

        {}
        <div className="bg-card/50 p-6 rounded-xl border border-border/50 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Disponibilità:</span>
            <span className="flex items-center text-sm text-green-600 font-medium">
              <Check className="size-4 mr-1" /> Disponibile
            </span>
          </div>

          <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide shadow-md transition-transform active:scale-[0.98]">
            <Link to="https://wa.me/390000000000" newTab>
              <MessageCircle className="size-5 mr-2" />
              Richiedi Preventivo
            </Link>
          </Button>
          
          <div className="flex items-center justify-center gap-4 pt-2">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary">
              <Heart className="size-4 mr-2" /> Salva
            </Button>
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary">
              <Share2 className="size-4 mr-2" /> Condividi
            </Button>
          </div>
        </div>

        {}
        <div className="space-y-3 text-sm text-muted-foreground pt-2">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-2">
              <Layers className="size-4 mt-0.5 shrink-0" />
              <span className="font-medium text-foreground">Categorie:</span>
              <div className="flex flex-wrap gap-1">
                {product.categories.map((cat, i) => (
                  <span key={cat.id} data-index={i}>
                    {cat.name}{i < product.categories.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-2">
              <Tag className="size-4 mt-0.5 shrink-0" />
              <span className="font-medium text-foreground">Tag:</span>
              <div className="flex flex-wrap gap-1">
                {product.tags.map((tag, i) => (
                  <span key={tag.id} data-index={i} className="hover:text-primary transition-colors cursor-pointer">
                    #{tag.name}{i < product.tags.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {}
        <Accordion type="single" collapsible className="w-full mt-4">
          <AccordionItem value="description">
            <AccordionTrigger className="text-base font-serif font-medium">
              Descrizione Completa
            </AccordionTrigger>
            <AccordionContent>
              <div 
                className="prose prose-stone max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: product.long_description || product.short_description || "Nessuna descrizione aggiuntiva disponibile." }}
              />
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="ingredients">
            <AccordionTrigger className="text-base font-serif font-medium">
              Ingredienti e Allergeni
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  I nostri prodotti sono realizzati artigianalmente nel nostro laboratorio di Sciacca.
                  Utilizziamo solo ingredienti freschi e selezionati della tradizione siciliana.
                </p>
                <p className="font-medium text-foreground mt-2">Possibili allergeni:</p>
                <p>
                  Glutine, Latte e derivati, Uova, Frutta a guscio (mandorle, pistacchi, noci), Soia.
                  Per informazioni specifiche su un prodotto, contattaci su WhatsApp.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="shipping">
            <AccordionTrigger className="text-base font-serif font-medium">
              Spedizione e Ritiro
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-3">
                <div className="flex items-start gap-2">
                  <Truck className="size-4 mt-1 shrink-0" />
                  <p>
                    Effettuiamo spedizioni in tutta Italia per i prodotti secchi (pasta di mandorla, biscotti).
                    Consegna in 24/48 ore lavorative.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Package className="size-4 mt-1 shrink-0" />
                  <p>
                    Per i prodotti freschi (cannoli, cassate) garantiamo la consegna refrigerata locale o il ritiro in sede a Sciacca.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}



export default function SingleProductTemplateSection() {
  
  const [currentQuery, setCurrentQuery] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      const query = window.wvcClient.getCurrentWPQueryParams();
      setCurrentQuery(query);
    }
  }, []);

  
  
  const safeQuery = currentQuery || { per_page: 1 };

  return (
    <section id="single-product-template-section" className="bg-background py-12 md:py-20">
      <div className="container mx-auto px-4">
        
        <EcommerceProductsProvider wp_query={safeQuery} postType="product" className="w-full">
          <SingleProductConsumer />
        </EcommerceProductsProvider>

      </div>
    </section>
  );
}